"use client";

import { useEffect, useMemo, useState, type MouseEvent } from "react";

type TocItem = {
    id: string;
    text: string;
    level: number;
};

const SCROLL_OFFSET = 140;

export default function BlogSidebar({ tocItems = [], dict }: { tocItems?: TocItem[]; dict?: { blog_sidebar?: { table_of_contents?: string } } }) {
    const [activeId, setActiveId] = useState<string>(tocItems[0]?.id || "");
    const itemIds = useMemo(() => tocItems.map((item) => item.id), [tocItems]);

    // Icindekiler degisince aktif basligi render sirasinda yeniden ayarla
    // (onceki deger yoksa ilk baslik; liste bosalirsa sifirla).
    const tocKey = itemIds.join("\n");
    const [prevTocKey, setPrevTocKey] = useState(tocKey);
    if (tocKey !== prevTocKey) {
        setPrevTocKey(tocKey);
        setActiveId(tocItems.length ? activeId || tocItems[0]?.id || "" : "");
    }

    useEffect(() => {
        if (!tocItems.length) {
            return;
        }

        const headings = tocItems
            .map((item) => document.getElementById(item.id))
            .filter((heading): heading is HTMLElement => Boolean(heading));

        if (!headings.length) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const visibleEntries = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

                if (visibleEntries.length) {
                    const nextId = visibleEntries[0].target.id;
                    setActiveId(nextId);
                    window.history.replaceState(null, "", `#${nextId}`);
                }
            },
            {
                rootMargin: `-${SCROLL_OFFSET}px 0px -60% 0px`,
                threshold: 0,
            }
        );

        headings.forEach((heading) => observer.observe(heading));

        const handleHashChange = () => {
            const hash = window.location.hash.replace(/^#/, "");
            if (hash && itemIds.includes(hash)) {
                setActiveId(hash);
            }
        };

        window.addEventListener("hashchange", handleHashChange);

        return () => {
            observer.disconnect();
            window.removeEventListener("hashchange", handleHashChange);
        };
    }, [itemIds, tocItems]);

    const handleClick = (id: string) => (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        const target = document.getElementById(id);
        if (!target) {
            return;
        }

        const top = target.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
        window.history.replaceState(null, "", `#${id}`);
        window.scrollTo({
            top,
            behavior: "smooth",
        });
        setActiveId(id);
    };

    if (!tocItems.length) {
        return null;
    }

    return (
        <aside className="w-full lg:w-[350px] space-y-8 sticky top-24">
            <div className="bg-white rounded-lg border border-gray-100 p-6 shadow-sm">
                <h3 className="text-[#0f4c81] font-bold text-[18px] mb-4 border-b border-gray-100 pb-2">
                    {dict?.blog_sidebar?.table_of_contents || "Table Of Contents"}
                </h3>
                <div className="flex max-h-[420px] flex-col space-y-3 overflow-y-auto pr-2">
                    {tocItems.map((item) => {
                        const isActive = item.id === activeId;

                        return (
                            <div
                                key={item.id}
                                className={`pl-3 border-l-2 transition-colors ${isActive ? "border-[#00AEEF]" : "border-transparent hover:border-gray-200"} ${item.level >= 3 ? `ml-${(item.level - 2) * 2}` : ""}`}
                            >
                                <a
                                    href={`#${item.id}`}
                                    onClick={handleClick(item.id)}
                                    className={`block text-[14px] leading-snug transition-colors ${isActive ? "text-[#0f4c81] font-semibold" : "text-gray-500 hover:text-[#0f4c81]"}`}
                                >
                                    {item.text}
                                </a>
                            </div>
                        );
                    })}
                </div>
            </div>
        </aside>
    );
}
